/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isComplexArray from '@stdlib/array-base-assert-is-complex-typed-array';
import isBooleanArray from '@stdlib/array-base-assert-is-booleanarray';
import iterationOrder from '@stdlib/ndarray-base-iteration-order';
import minmaxViewBufferIndex from '@stdlib/ndarray-base-minmax-view-buffer-index';
import ndarray2object from '@stdlib/ndarray-base-ndarraylike2object';
import reinterpretComplex from '@stdlib/strided-base-reinterpret-complex';
import reinterpretBoolean from '@stdlib/strided-base-reinterpret-boolean';
import gscal from '@stdlib/blas-base-gscal';
import blockedaccessorsome2d from './2d_blocked_accessors.js';
import blockedaccessorsome3d from './3d_blocked_accessors.js';
import blockedaccessorsome4d from './4d_blocked_accessors.js';
import blockedaccessorsome5d from './5d_blocked_accessors.js';
import blockedaccessorsome6d from './6d_blocked_accessors.js';
import blockedaccessorsome7d from './7d_blocked_accessors.js';
import blockedaccessorsome8d from './8d_blocked_accessors.js';
import blockedaccessorsome9d from './9d_blocked_accessors.js';
import blockedaccessorsome10d from './10d_blocked_accessors.js';
import blockedcomplexsome2d from './2d_blocked_complex.js';
import blockedcomplexsome3d from './3d_blocked_complex.js';
import blockedcomplexsome4d from './4d_blocked_complex.js';
import blockedcomplexsome5d from './5d_blocked_complex.js';
import blockedcomplexsome6d from './6d_blocked_complex.js';
import blockedcomplexsome7d from './7d_blocked_complex.js';
import blockedcomplexsome8d from './8d_blocked_complex.js';
import blockedcomplexsome9d from './9d_blocked_complex.js';
import blockedcomplexsome10d from './10d_blocked_complex.js';
import blockedsome2d from './2d_blocked.js';
import blockedsome3d from './3d_blocked.js';
import blockedsome4d from './4d_blocked.js';
import blockedsome5d from './5d_blocked.js';
import blockedsome6d from './6d_blocked.js';
import blockedsome7d from './7d_blocked.js';
import blockedsome8d from './8d_blocked.js';
import blockedsome9d from './9d_blocked.js';
import blockedsome10d from './10d_blocked.js';
import accessorsome0d from './0d_accessors.js';
import accessorsome1d from './1d_accessors.js';
import accessorsome2d from './2d_accessors.js';
import accessorsome3d from './3d_accessors.js';
import accessorsome4d from './4d_accessors.js';
import accessorsome5d from './5d_accessors.js';
import accessorsome6d from './6d_accessors.js';
import accessorsome7d from './7d_accessors.js';
import accessorsome8d from './8d_accessors.js';
import accessorsome9d from './9d_accessors.js';
import accessorsome10d from './10d_accessors.js';
import accessorsomend from './nd_accessors.js';
import complexsome0d from './0d_complex.js';
import complexsome1d from './1d_complex.js';
import complexsome2d from './2d_complex.js';
import complexsome3d from './3d_complex.js';
import complexsome4d from './4d_complex.js';
import complexsome5d from './5d_complex.js';
import complexsome6d from './6d_complex.js';
import complexsome7d from './7d_complex.js';
import complexsome8d from './8d_complex.js';
import complexsome9d from './9d_complex.js';
import complexsome10d from './10d_complex.js';
import complexsomend from './nd_complex.js';
import some0d from './0d.js';
import some1d from './1d.js';
import some2d from './2d.js';
import some3d from './3d.js';
import some4d from './4d.js';
import some5d from './5d.js';
import some6d from './6d.js';
import some7d from './7d.js';
import some8d from './8d.js';
import some9d from './9d.js';
import some10d from './10d.js';
import somend from './nd.js';


// VARIABLES //

var SOME = [
	some0d,
	some1d,
	some2d,
	some3d,
	some4d,
	some5d,
	some6d,
	some7d,
	some8d,
	some9d,
	some10d,
	somend
];
var ACCESSOR_SOME = [
	accessorsome0d,
	accessorsome1d,
	accessorsome2d,
	accessorsome3d,
	accessorsome4d,
	accessorsome5d,
	accessorsome6d,
	accessorsome7d,
	accessorsome8d,
	accessorsome9d,
	accessorsome10d,
	accessorsomend
];
var COMPLEX_SOME = [
	complexsome0d,
	complexsome1d,
	complexsome2d,
	complexsome3d,
	complexsome4d,
	complexsome5d,
	complexsome6d,
	complexsome7d,
	complexsome8d,
	complexsome9d,
	complexsome10d,
	complexsomend
];
var BLOCKED_SOME = [
	blockedsome2d, // 0
	blockedsome3d,
	blockedsome4d,
	blockedsome5d,
	blockedsome6d,
	blockedsome7d,
	blockedsome8d,
	blockedsome9d,
	blockedsome10d // 8
];
var BLOCKED_ACCESSOR_SOME = [
	blockedaccessorsome2d, // 0
	blockedaccessorsome3d,
	blockedaccessorsome4d,
	blockedaccessorsome5d,
	blockedaccessorsome6d,
	blockedaccessorsome7d,
	blockedaccessorsome8d,
	blockedaccessorsome9d,
	blockedaccessorsome10d // 8
];
var BLOCKED_COMPLEX_SOME = [
	blockedcomplexsome2d, // 0
	blockedcomplexsome3d,
	blockedcomplexsome4d,
	blockedcomplexsome5d,
	blockedcomplexsome6d,
	blockedcomplexsome7d,
	blockedcomplexsome8d,
	blockedcomplexsome9d,
	blockedcomplexsome10d // 8
];
var MAX_DIMS = SOME.length - 1;


// MAIN //

/**
* Tests whether at least `n` elements in an ndarray are truthy.
*
* ## Notes
*
* -   A provided ndarray should be an object with the following properties:
*
*     -   **dtype**: data type.
*     -   **data**: data buffer.
*     -   **shape**: dimensions.
*     -   **strides**: stride lengths.
*     -   **offset**: index offset.
*     -   **order**: specifies whether an ndarray is row-major (C-style) or column major (Fortran-style).
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing an input ndarray and a zero-dimensional ndarray specifying the minimum number of elements in the input ndarray that must satisfy the predicate function
* @returns {boolean} boolean indicating whether at least `n` elements are truthy
*
* @example
* import scalar2ndarray from '@stdlib/ndarray-from-scalar';
* import Float64Array from '@stdlib/array-float64';
*
* // Create a data buffer:
* var xbuf = new Float64Array( [ 1.0, 2.0, 3.0, 4.0, 5.0, 0.0, 7.0, 8.0, 9.0, 10.0, 11.0, 12.0 ] );
*
* // Define the shape of the input array:
* var shape = [ 3, 1, 2 ];
*
* // Define the array strides:
* var sx = [ 4, 4, 1 ];
*
* // Define the index offset:
* var ox = 1;
*
* // Create an input ndarray-like object:
* var x = {
*     'dtype': 'float64',
*     'data': xbuf,
*     'shape': shape,
*     'strides': sx,
*     'offset': ox,
*     'order': 'row-major'
* };
*
* // Define the success criterion:
* var n = scalar2ndarray( 3, {
*     'dtype': 'generic'
* });
*
* // Test elements:
* var out = some( [ x, n ] );
* // returns true
*/
function some( arrays ) {
	var isCmplx;
	var ndims;
	var xmmv;
	var shx;
	var iox;
	var len;
	var sx;
	var ox;
	var ns;
	var N;
	var x;
	var n;
	var d;
	var i;

	// Unpack the ndarray and standardize ndarray meta data:
	x = ndarray2object( arrays[ 0 ] );
	n = ndarray2object( arrays[ 1 ] );

	shx = x.shape;
	ndims = shx.length;

	// Resolve the success criterion:
	N = n.accessors[ 0 ]( n.data, n.offset );

	// Check for known array types which can be reinterpreted for better iteration performance...
	if ( isBooleanArray( x.data ) ) {
		x.data = reinterpretBoolean( x.data, 0 );
		x.accessorProtocol = false;
	} else if ( isComplexArray( x.data ) ) {
		x.data = reinterpretComplex( x.data, 0 );
		x.accessorProtocol = false;
		x.strides = gscal( ndims, 2, x.strides, 1 );
		x.offset *= 2;
		isCmplx = true;
	}
	// Determine whether we can avoid iteration altogether...
	if ( ndims === 0 ) {
		if ( x.accessorProtocol ) {
			return ACCESSOR_SOME[ ndims ]( x, N );
		}
		if ( isCmplx ) {
			return COMPLEX_SOME[ ndims ]( x, N );
		}
		return SOME[ ndims ]( x, N );
	}
	// Compute the number of elements and the number of singleton dimensions...
	len = 1; // number of elements
	ns = 0;  // number of singleton dimensions
	for ( i = 0; i < ndims; i++ ) {
		d = shx[ i ];

		// Note that, if one of the dimensions is `0`, the length will be `0`...
		len *= d;

		// Check whether the current dimension is a singleton dimension...
		if ( d === 1 ) {
			ns += 1;
		}
	}
	// Check whether we were provided an empty ndarray...
	if ( len === 0 ) {
		return false;
	}
	// Determine whether the ndarray is one-dimensional and thus readily translates to a one-dimensional strided array...
	if ( ndims === 1 ) {
		if ( x.accessorProtocol ) {
			return ACCESSOR_SOME[ ndims ]( x, N );
		}
		if ( isCmplx ) {
			return COMPLEX_SOME[ ndims ]( x, N );
		}
		return SOME[ ndims ]( x, N );
	}
	sx = x.strides;

	// Determine whether the ndarray has only **one** non-singleton dimension (e.g., ndims=4, shape=[10,1,1,1]) so that we can treat an ndarray as being equivalent to a one-dimensional strided array...
	if ( ns === ndims-1 ) {
		// Get the index of the non-singleton dimension...
		for ( i = 0; i < ndims; i++ ) {
			if ( shx[ i ] !== 1 ) {
				break;
			}
		}
		x.shape = [ shx[i] ];
		x.strides = [ sx[i] ];
		if ( x.accessorProtocol ) {
			return ACCESSOR_SOME[ 1 ]( x, N );
		}
		if ( isCmplx ) {
			return COMPLEX_SOME[ 1 ]( x, N );
		}
		return SOME[ 1 ]( x, N );
	}
	iox = iterationOrder( sx ); // +/-1

	// Determine whether we can avoid blocked iteration...
	if ( iox !== 0 ) {
		// Determine the minimum and maximum linear indices which are accessible by the array view:
		xmmv = minmaxViewBufferIndex( shx, sx, x.offset );

		// Determine whether we can ignore shape (and strides) and treat the ndarray as a linear one-dimensional strided array...
		if ( len === ( xmmv[1]-xmmv[0]+1 ) || ( isCmplx && len*2 === ( xmmv[1]-xmmv[0]+2 ) ) ) { // eslint-disable-line max-len
			// Note: the above is equivalent to @stdlib/ndarray/base/assert/is-contiguous, but in-lined so we can retain computed values...
			if ( iox === 1 ) {
				ox = xmmv[ 0 ];
			} else {
				ox = xmmv[ 1 ];
			}
			x.shape = [ len ];
			x.strides = [ ( isCmplx ) ? iox*2 : iox ];
			x.offset = ox;
			if ( x.accessorProtocol ) {
				return ACCESSOR_SOME[ 1 ]( x, N );
			}
			if ( isCmplx ) {
				return COMPLEX_SOME[ 1 ]( x, N );
			}
			return SOME[ 1 ]( x, N );
		}
		// The ndarray is non-contiguous, so we cannot directly use one-dimensional array functionality...

		// Determine whether we can use simple nested loops...
		if ( ndims <= MAX_DIMS ) {
			// So long as iteration always moves in the same direction (i.e., no mixed sign strides), we can leverage cache-optimal (i.e., normal) nested loops without resorting to blocked iteration...
			if ( x.accessorProtocol ) {
				return ACCESSOR_SOME[ ndims ]( x, N );
			}
			if ( isCmplx ) {
				return COMPLEX_SOME[ ndims ]( x, N );
			}
			return SOME[ ndims ]( x, N );
		}
		// Fall-through to blocked iteration...
	}
	// At this point, we're either dealing with a non-contiguous n-dimensional array or a high dimensional n-dimensional array, so our only hope is that we can still perform blocked iteration...

	// Determine whether we can perform blocked iteration...
	if ( ndims <= MAX_DIMS ) {
		if ( x.accessorProtocol ) {
			return BLOCKED_ACCESSOR_SOME[ ndims-2 ]( x, N );
		}
		if ( isCmplx ) {
			return BLOCKED_COMPLEX_SOME[ ndims-2 ]( x, N );
		}
		return BLOCKED_SOME[ ndims-2 ]( x, N );
	}
	// Fall-through to linear view iteration without regard for how data is stored in memory (i.e., take the slow path)...
	if ( x.accessorProtocol ) {
		return accessorsomend( x, N );
	}
	if ( isCmplx ) {
		return complexsomend( x, N );
	}
	return somend( x, N );
}


// EXPORTS //

export default some;
